import {
  cn,
  ModeToggle,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@devhop/ui";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { Store } from "lucide-react";
import { usePOSContext } from "@/modules/pos/presentation/context/POSContext";
import { OfflineSyncStatusBadge } from "@/modules/pos/presentation/ui/OfflineSyncStatusBadge";
import { ShiftStatusIndicator } from "@/modules/pos/presentation/ui/ShiftStatusIndicator";
import { POS_MORE_NAV } from "./pos-nav-config";

type POSMobileMenuSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function POSMobileMenuSheet({
  open,
  onOpenChange,
}: POSMobileMenuSheetProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { selection } = usePOSContext();

  const closeAnd = (action: () => void) => {
    onOpenChange(false);
    action();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="max-h-[85svh] rounded-t-2xl border-t p-0 pb-[env(safe-area-inset-bottom,0px)]"
      >
        <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-muted-foreground/30" />

        <SheetHeader className="border-b px-4 py-3 text-left">
          <SheetTitle className="font-heading text-base">ເມນູເພີ່ມເຕີມ</SheetTitle>
          {selection && (
            <div className="flex items-center gap-1.5 text-muted-foreground text-xs">
              <Store className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{selection.branchName}</span>
              {selection.deviceName ? (
                <>
                  <span>•</span>
                  <span className="truncate">{selection.deviceName}</span>
                </>
              ) : null}
            </div>
          )}
        </SheetHeader>

        <div className="overflow-y-auto px-4 py-3">
          <div className="mb-4 rounded-xl border bg-muted/20 p-3">
            <div className="mb-2 font-medium text-muted-foreground text-xs uppercase tracking-wide">
              ສະຖານະກະ
            </div>
            <ShiftStatusIndicator />
            <div className="mt-3">
              <OfflineSyncStatusBadge compact />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {POS_MORE_NAV.map((item) => {
              const Icon = item.icon;
              const active = item.isActive?.(pathname) ?? false;

              return (
                <button
                  key={item.key}
                  type="button"
                  className={cn(
                    "flex min-h-[4.5rem] flex-col items-start gap-2 rounded-xl border p-3 text-left transition-colors",
                    active
                      ? "border-primary/40 bg-primary/10 text-primary"
                      : "bg-card hover:bg-muted/40",
                  )}
                  onClick={() => {
                    if (item.externalHref) {
                      closeAnd(() => {
                        window.open(item.externalHref, "_blank");
                      });
                      return;
                    }
                    if (item.navigate) {
                      closeAnd(() => {
                        navigate(item.navigate!());
                      });
                    }
                  }}
                >
                  <span
                    className={cn(
                      "flex h-9 w-9 items-center justify-center rounded-lg",
                      active
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground",
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block font-medium text-sm leading-snug">
                      {item.label}
                    </span>
                    <span className="mt-0.5 block text-muted-foreground text-[11px] leading-snug">
                      {item.description}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mt-4 flex items-center justify-between rounded-xl border bg-card px-3 py-2.5">
            <span className="font-medium text-sm">ໂໝດສີ</span>
            <ModeToggle />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
